"use client";

import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { MoreVerticalIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import CommonTableList from "@/components/common/TableList";
import CommonDelete from "@/components/common/Delete";
import RefuelingCreate from "./Create";
import { RefuelData } from "@/interface/entityInterface";
import { RefuelDetails } from "./RefuelingDetails";
import { format } from "date-fns";
import RefuelingEdit from "./RefuelingEdit";
import CommonList from "@/components/common/List";

export type Order = {
  id: number;
  client_name: string;
  client_phone: string;
  pickup: string;
  dropoff: string;
  status:
    | "NEW_ORDER"
    | "IN_PROGRESS"
    | "ASSIGNED"
    | "ACCEPTED"
    | "REJECTED"
    | "CANCELLED"
    | "COMPLETED";
  created_at: string;
  odometer: number;
  date: string;
  time: string;
};

export const RefuelingList = () => {
  const [open, setOpen] = React.useState(false);
  const [refuel, setRefuel] = React.useState<RefuelData | null>(null);

  const handleOpen = (e: any, row: RefuelData) => {
    setRefuel(row);
    setOpen(true);
  };

  const columns = React.useMemo<ColumnDef<RefuelData>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      {
        accessorKey: "id",
        header: "ID",
        cell: ({ row }) => (
          <div className="capitalize">{row.getValue("id")}</div>
        ),
      },
      {
        accessorFn: (row) => row?.vehicle.license_plate || "",
        accessorKey: "vehicle",
        id: "vehicle",
        header: "Vehicle",
        cell: ({ row }) => {
          const vehicle = row.getValue("vehicle") as any;
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">{vehicle}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "date",
        header: "Date",
        cell: ({ row }) => (
          <div className="flex flex-col text-sm space-y-1">
            <span className="font-medium">
              {format(row.getValue("date"), "dd MMMM yyyy HH:mm")}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "odometer",
        header: "Odometer",
        cell: ({ row }) => (
          <div className="flex flex-col text-sm space-y-1">
            <span className="font-medium">{row.getValue("odometer")}</span>
          </div>
        ),
      },
      {
        id: "actions",
        cell: ({ row }) => {
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
                  size="icon"
                >
                  <MoreVerticalIcon />
                  <span className="sr-only">Open menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-32">
                <RefuelingEdit prop={row.original.id} />
                <DropdownMenuSeparator />
                <CommonDelete
                  resource="refueling"
                  id={row.getValue("id")}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [],
  );

  return (
    <>
      <CommonList>
        <CommonTableList
        columns={columns}
        filterSearch="vehicle"
        create={<RefuelingCreate />}
        rowClick={handleOpen}
      />
      </CommonList>
      {open && <RefuelDetails open={open} setOpen={setOpen} refuel={refuel} />}
    </>
  );
};

export default RefuelingList;
